import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "@/types/api";
import { getSessionHint, rememberSession } from "@/lib/sessionHint";
import { can as canDo, type CapabilityNeed } from "./capabilities";
import * as authApi from "./api";

// Persist a non-sensitive "recently signed in here" hint so a returning user can
// be greeted by their shop name: never hardcoded, only what the API supplied.
function remember(u: User | null) {
  if (u) rememberSession({ userName: u.name, shopName: u.tenantName });
}

type Status = "loading" | "authed" | "guest";

interface AuthContextValue {
  status: Status;
  user: User | null;
  /**
   * Does the signed-in user hold this capability (an array means any-of)? The
   * one gate for rendering: hide a nav leaf, a button, a settings tab. Fails
   * closed while /auth/me is in flight and for a guest, so nothing flashes into
   * view before the permission list arrives. The server's 403 is still the real
   * boundary (§6.3) - this only decides what is drawn.
   */
  can: (need: CapabilityNeed) => boolean;
  /** Re-fetch /auth/me (call after a successful login or 2FA verify). */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);

  const refresh = useCallback(async () => {
    const u = await authApi.me();
    remember(u);
    setUser(u);
    setStatus(u ? "authed" : "guest");
  }, []);

  useEffect(() => {
    // The session is an HttpOnly cookie, so JS can't read it; confirming a
    // session means asking the server (/auth/me). Only do that when there's a
    // reason to: a device that has signed in here before carries a (non-secret)
    // hint. A fresh / never-logged-in visitor has none, so we resolve to "guest"
    // with no request at all: no wasted /auth/me on public pages (landing, etc.).
    // A returning user still gets the one confirming probe (their cookie may
    // still be valid), which auto-restores their session on reload.
    if (!getSessionHint()) {
      setStatus("guest");
      return;
    }
    const ctrl = new AbortController();
    authApi
      .me(ctrl.signal)
      .then((u) => {
        if (ctrl.signal.aborted) return; // stale probe (StrictMode remount)
        remember(u);
        setUser(u);
        setStatus(u ? "authed" : "guest");
      })
      .catch(() => {
        // An aborted probe (effect cleanup) is not a failed session; ignoring it
        // avoids a transient "guest" flash that could bounce a returning user.
        if (ctrl.signal.aborted) return;
        setStatus("guest");
      });
    return () => ctrl.abort();
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setStatus("guest");
    }
  }, []);

  const can = useCallback(
    (need: CapabilityNeed) => canDo(user?.capabilities, need),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, can, refresh, logout }),
    [status, user, can, refresh, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
